import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';

type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost';
type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  children: ReactNode;
  className?: string;
}

const VARIANT_CLASSES: Record<ButtonVariant, string> = {
  primary:
    'bg-teal text-white border border-teal hover:bg-teal-dark hover:border-teal-dark shadow-sm',
  secondary:
    'bg-white text-ink border border-stone hover:bg-ghost',
  danger:
    'bg-danger text-white border border-danger hover:opacity-90 shadow-sm',
  ghost:
    'bg-transparent text-ink border border-transparent hover:bg-ash',
};

// Size sm = 36px tall — BELOW the 44px WCAG tap target. Use only for
// dense / stepper / icon-only buttons where tap targets are otherwise
// generous (e.g., qty +/- inside a product card with a large hit area).
// md and lg both meet WCAG 2.5.5 minimum (44×44).
const SIZE_CLASSES: Record<ButtonSize, string> = {
  sm: 'h-9 px-3 text-sm font-bold gap-1.5',
  md: 'h-11 px-4 text-base font-bold gap-2',
  lg: 'h-12 px-5 text-base font-black gap-2',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  function Button(
    {
      variant = 'primary',
      size = 'md',
      loading = false,
      disabled,
      children,
      className = '',
      type = 'button',
      ...rest
    },
    ref,
  ) {
    return (
      <button
        ref={ref}
        type={type}
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        className={`inline-flex items-center justify-center rounded-xl tracking-tight whitespace-nowrap transition-all active:scale-[0.97] disabled:opacity-50 disabled:cursor-not-allowed ${VARIANT_CLASSES[variant]} ${SIZE_CLASSES[size]} ${className}`}
        {...rest}
      >
        {loading && (
          <span
            className="inline-block w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"
            aria-hidden="true"
          />
        )}
        {children}
      </button>
    );
  },
);
